interface Props {
  /** Live search text (URL state). Search-as-you-type — every keystroke fires onChange. */
  value: string;
  /** Fires on every keystroke. Wire to URL state's `q` so results update live. */
  onChange: (q: string) => void;
  /** Fires on Enter with the current (trimmed) text. Wire to push into qPills + clear `q`.
   *  Caller is responsible for clearing the input (by setting value=""). */
  onCommit: (q: string) => void;
  /** Placeholder text + aria-label context. Defaults to the request-screen wording. */
  placeholder?: string;
  /** Accessible label, e.g. "Search requests" / "Search tasks". */
  ariaLabel?: string;
}

/**
 * Search box with both search-as-you-type AND commit-on-Enter semantics:
 * - typing flows live into URL `q` → results filter immediately (snapshot is in-memory cached
 *   so each keystroke is cheap)
 * - pressing Enter commits the current text into the active filter chips (a pill in
 *   ActiveChips) and clears the input so the user can start a new search. Multiple pills
 *   accumulate and AND together.
 */
export function SearchInput({
  value,
  onChange,
  onCommit,
  placeholder = "Search workers, clients, WO #, request ID — Enter to pin as filter",
  ariaLabel = "Search requests",
}: Props) {
  const commit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onCommit(trimmed);
  };

  return (
    <div className="search">
      <span className="material-symbols-rounded icon">search</span>
      <input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
        }}
        aria-label={ariaLabel}
      />
    </div>
  );
}
